<template>
  <vs-popup
    class="vs-con-loading__container create-shortcut-popup-modal"
    id="popup"
    title="CREATE SHORTCUT"
    @close="open = false"
    :active.sync="open"
    :buttons-hidden="true"
  >
    <div id="create-shortcut-popup" class="create-shortcut-content">
      <div class="vx-row">
        <!-- Left Config Column -->
        <div class="vx-col md:w-5/12 w-full mb-4 md:mb-0">
          <div class="popup-section-card h-full flex flex-col justify-between">
            <div>
              <!-- Shortcut Function Selector -->
              <div class="form-group-item">
                <label class="section-label">
                  Shortcut Function <span class="text-danger">*</span>
                </label>
                <v-select
                  :filter-by="myFilter"
                  :clearable="false"
                  label="name"
                  v-model="createForm.id"
                  :reduce="option => option.id"
                  :options="available_shortcuts"
                  placeholder="Select a module..."
                  :dir="$vs.rtl ? 'rtl' : 'ltr'"
                  class="custom-v-select"
                >
                  <template v-slot:option="option">
                    <div class="py-1">
                      <span class="font-medium text-dark block">{{ option.name }}</span>
                      <span class="text-xs opacity-75 block">{{ option.description }}</span>
                    </div>
                  </template>
                </v-select>
              </div>

              <!-- Color Picker & Swatches -->
              <div class="form-group-item">
                <label class="section-label">
                  Accent Color
                </label>
                <!-- Color Swatches Container -->
                <div class="color-swatches-wrapper">
                  <span
                    v-for="preset in colorPresets"
                    :key="preset"
                    class="color-swatch cursor-pointer rounded-full"
                    :class="{ 'active-swatch': createForm.color.toLowerCase() === preset.toLowerCase() }"
                    :style="{ background: preset }"
                    @click="createForm.color = preset"
                  ></span>
                  <!-- Custom Color Input -->
                  <div class="custom-color-input-wrapper">
                    <input
                      v-model="createForm.color"
                      type="color"
                      class="custom-color-picker"
                      title="Choose custom color"
                    >
                  </div>
                </div>
              </div>

              <!-- Selected Icon & Color Preview -->
              <div class="form-group-item mb-0">
                <label class="section-label">
                  Live Preview
                </label>
                <div
                  class="preview-box rounded-xl flex items-center"
                  :style="{ border: `1px solid ${rgb_color_border}` }"
                >
                  <div
                    class="preview-icon-wrapper flex items-center justify-center rounded-xl mr-3 flex-shrink-0"
                    :style="{ background: rgb_color, color: hex_color }"
                  >
                    <feather-icon :icon="createForm.icon" svgClasses="h-6 w-6" :style="{ color: hex_color }" />
                  </div>
                  <div class="truncate">
                    <h5 class="preview-title font-bold text-sm leading-tight truncate mb-1">
                      {{ selectedShortcutObj ? selectedShortcutObj.name : 'Select Shortcut' }}
                    </h5>
                    <p class="preview-desc text-xs opacity-75 truncate mb-0">
                      {{ selectedShortcutObj ? selectedShortcutObj.description : 'Preview description' }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Button -->
            <div class="border-top-custom mt-4 pt-3">
              <vs-button
                @click.stop="onClickCreate"
                button="submit"
                class="w-full"
                :disabled="!createForm.id"
                color="primary"
                type="filled"
                icon="icon-plus"
                icon-pack="feather"
              >
                Create Shortcut
              </vs-button>
            </div>
          </div>
        </div>

        <!-- Right Icon Picker Column -->
        <div class="vx-col md:w-7/12 w-full">
          <div class="popup-section-card h-full flex flex-col">
            <div class="flex items-center justify-between mb-2">
              <label class="section-label mb-0">
                Choose Icon
              </label>
              <span class="text-xs opacity-75">
                {{ formatted_icon_list.length }} icons
              </span>
            </div>

            <vs-input
              v-model="icon_search"
              class="w-full search-icon-input"
              icon-pack="feather"
              icon="icon-search"
              icon-after
              placeholder="Search icons (e.g. box, user, cart)..."
            />

            <!-- Icon Grid -->
            <div class="icon-grid-container p-2 rounded-lg mt-3">
              <div class="grid grid-cols-6 sm:grid-cols-8 gap-2">
                <div
                  v-for="(icon, index) in formatted_icon_list"
                  :key="index"
                  class="icon-grid-item flex items-center justify-center rounded-lg p-2 cursor-pointer transition-all"
                  :class="{ 'selected-icon-item': createForm.icon === icon.value }"
                  :style="createForm.icon === icon.value ? { background: rgb_color, borderColor: hex_color } : {}"
                  @click.stop="onClickIcon(icon)"
                >
                  <vx-tooltip :text="icon.name" position="top">
                    <feather-icon
                      :icon="icon.value"
                      svgClasses="h-5 w-5"
                      :style="{ color: createForm.icon === icon.value ? hex_color : 'inherit' }"
                    />
                  </vx-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import vSelect from 'vue-select'
import { shortcut_list } from '@/constants/shortcuts'
import { icon_list } from '@/constants/icons'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const ShortcutsRepository = RepositoryFactory.get('shortcuts')
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'
import _color from '@/assets/utils/color.js'

function initialState() {
  return {
    open: false,
    shortcut_list: shortcut_list,
    icon_list: icon_list,
    icon_search: '',
    colorPresets: [
      '#7367F0', // Primary Vuexy
      '#28C76F', // Success Emerald
      '#EA5455', // Danger Red
      '#FF9F43', // Warning Orange
      '#00CFE8', // Cyan
      '#9C27B0', // Purple
      '#3F51B5', // Indigo
      '#E91E63', // Pink
      '#1E1E1E', // Dark
    ],
    createForm: {
      color: '#7367F0',
      icon: 'BoxIcon',
      id: ''
    },
    exist_shortcut_ids: []
  }
}

export default {
  name: 'CreateShortcutPopUp',
  data() {
    return initialState()
  },
  mixins: [SuccessMessage, ErrorMessages],
  components: {
    'v-select': vSelect,
  },
  methods: {
    async openPopup(formatted_short_list) {
      try {
        await Object.assign(this.$data, initialState())
        this.exist_shortcut_ids = formatted_short_list.map((x) => x.id)
        this.open = true
      } catch (error) {
      }
    },
    myFilter: (option, label, search) => {
      let temp = search.toLowerCase()
      return option.name.toLowerCase().indexOf(temp) > -1 ||
        option.description.toLowerCase().indexOf(temp) > -1
    },
    async onClickCreate() {
      if (!this.createForm.id) return

      await this.$vs.loading({
        container: '#create-shortcut-popup',
        scale: 0.45
      })
      try {
        await ShortcutsRepository.createShortcut({ shortcuts: [this.createForm] })
        this.$parent.refreshData()
        this.open = false
      } catch (error) {
        this.convertAndNotifyError(error)
      }
      this.$vs.loading.close('#create-shortcut-popup > .con-vs-loading')
    },
    onClickIcon(icon) {
      this.createForm.icon = icon.value
    },
  },
  computed: {
    rgb_color: function () {
      return _color.getColor(this.createForm.color, .12)
    },
    rgb_color_border: function () {
      return _color.getColor(this.createForm.color, .3)
    },
    hex_color: function () {
      return this.createForm.color
    },
    formatted_icon_list: function () {
      if (!this.icon_search) return this.icon_list
      const q = this.icon_search.toLowerCase()
      return this.icon_list.filter((x) => x.name.toLowerCase().includes(q) || x.value.toLowerCase().includes(q))
    },
    available_shortcuts: function () {
      return this.shortcut_list.filter((x) => {
        return !this.exist_shortcut_ids.includes(x.id) && (x.permissions.length === 0 || this.$canAny(x.permissions))
      })
    },
    selectedShortcutObj: function () {
      return this.shortcut_list.find(x => x.id === this.createForm.id)
    }
  }
}
</script>

<style lang="scss" scoped>
.create-shortcut-popup-modal {
  ::v-deep .vs-popup {
    width: 780px !important;
    max-width: 95vw !important;
    border-radius: 14px;
  }
}

.create-shortcut-content {
  padding: 0.25rem 0;
}

.popup-section-card {
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  padding: 1.25rem 1.25rem;
}

.form-group-item {
  margin-bottom: 18px;
}

.section-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #5d6778;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}

.color-swatches-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0 -5px;
  padding: 2px 0;
}

.color-swatch {
  width: 28px;
  height: 28px;
  margin: 5px 6px;
  display: inline-block;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;

  &:hover {
    transform: scale(1.18);
  }

  &.active-swatch {
    transform: scale(1.2);
    box-shadow: 0 0 0 2.5px rgba(var(--vs-primary), 1);
  }
}

.custom-color-input-wrapper {
  position: relative;
  width: 28px;
  height: 28px;
  margin: 5px 6px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  flex-shrink: 0;

  .custom-color-picker {
    position: absolute;
    top: -8px;
    left: -8px;
    width: 44px;
    height: 44px;
    border: none;
    cursor: pointer;
  }
}

.preview-box {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 12px 14px;
}

.preview-icon-wrapper {
  width: 44px;
  height: 44px;
}

.preview-title {
  color: #2c2c2c;
}

.preview-desc {
  color: #727b87;
}

.border-top-custom {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.search-icon-input {
  margin-top: 4px;
}

.icon-grid-container {
  max-height: 260px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }
}

.icon-grid-item {
  color: #64748b;
  border: 1px solid transparent;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
    color: #1e293b;
    transform: scale(1.1);
  }

  &.selected-icon-item {
    font-weight: bold;
    border-width: 1px;
    transform: scale(1.08);
  }
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .popup-section-card {
    background: #171e49;
    border-color: rgba(255, 255, 255, 0.08);
  }

  .section-label {
    color: #9aa2ba;
  }

  .preview-box {
    background: #10163a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .preview-title {
    color: #ebeefd;
  }

  .preview-desc {
    color: #9aa2ba;
  }

  .color-swatch {
    border-color: #171e49;
  }

  .custom-color-input-wrapper {
    border-color: #171e49;
  }

  .border-top-custom {
    border-top-color: rgba(255, 255, 255, 0.08);
  }

  .icon-grid-container {
    background: #10163a;
    border-color: rgba(255, 255, 255, 0.08);

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .icon-grid-item {
    color: #9aa2ba;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
      color: #ebeefd;
    }
  }
}
</style>


